/**
 * Model Fetching Service (Venice-only)
 *
 * Fetches the live model catalogue dynamically from Venice via the
 * `venice-proxy` edge function (API key stays server-side). No model names,
 * capabilities or prices are hardcoded or stored in the DB — the Venice
 * `/models` endpoint is the single source of truth, refreshed on demand and
 * cached for a short TTL by the proxy.
 */

import { supabase } from '../lib/supabaseClient';

// ─── Venice API shapes ──────────────────────────────────────────────────────

interface VeniceModelSpec {
  name?: string;
  offline?: boolean;
  description?: string;
  pricing?: {
    input?: { usd?: number };
    output?: { usd?: number };
    generation?: { usd?: number };
    image?: { usd?: number };
  };
  capabilities?: {
    supportsFunctionCalling?: boolean;
    supportsVision?: boolean;
    supportsReasoning?: boolean;
    supportsReasoningEffort?: boolean;
    supportsResponseSchema?: boolean;
  };
  traits?: string[];
  availableContextTokens?: number;
}

interface VeniceModel {
  id: string;
  created?: number;
  type: 'text' | 'image' | 'embedding' | string;
  model_spec?: VeniceModelSpec;
}

// ─── Unified model type ─────────────────────────────────────────────────────

export interface UnifiedModel {
  id: string;
  name: string;
  type: 'text' | 'image';
  description?: string;
  pricing?: {
    input?: number;     // USD per 1M input tokens
    output?: number;    // USD per 1M output tokens
    perImage?: number;  // USD per image
  };
  capabilities?: {
    supportsReasoning?: boolean;
    supportsReasoningEffort?: boolean;
    supportsVision?: boolean;
    supportsFunctionCalling?: boolean;
    supportsResponseSchema?: boolean;
  };
  traits?: string[];
  isOffline?: boolean;
  isDefault?: boolean;
  contextLength?: number;
  createdAt?: number;
}

// ─── Cache ───────────────────────────────────────────────────────────────────

interface ModelCache {
  text: UnifiedModel[];
  image: UnifiedModel[];
  traits: Record<string, string>;
  lastFetched: { text: Date | null; image: Date | null; traits: Date | null };
}

const modelCache: ModelCache = {
  text: [],
  image: [],
  traits: {},
  lastFetched: { text: null, image: null, traits: null },
};

// ─── Fetchers ────────────────────────────────────────────────────────────────

/**
 * Fetch Venice text/image models via the proxy. Returns the live catalogue —
 * nothing is stored server-side beyond the proxy's short-TTL in-memory cache.
 */
export async function fetchVeniceModels(
  type: 'text' | 'image',
): Promise<{ success: boolean; models: UnifiedModel[]; error?: string }> {
  try {
    const { data, error } = await supabase.functions.invoke('venice-proxy', {
      body: { action: 'listModels', options: { type } },
    });

    if (error) throw new Error(error.message);
    if (!data?.success) throw new Error(data?.error || 'Failed to fetch Venice models');

    const models: UnifiedModel[] = (data.models || [])
      .filter((m: VeniceModel) => !m.model_spec?.offline)
      .map((m: VeniceModel) => {
        const spec = m.model_spec || {};
        const pricing = spec.pricing || {};
        return {
          id: m.id,
          name: spec.name || m.id,
          type: type,
          description: spec.description,
          pricing: {
            input: pricing.input?.usd,
            output: pricing.output?.usd,
            perImage: pricing.generation?.usd ?? pricing.image?.usd,
          },
          capabilities: spec.capabilities,
          traits: spec.traits || [],
          isOffline: spec.offline,
          isDefault: (spec.traits || []).includes('default'),
          contextLength: spec.availableContextTokens,
          createdAt: m.created,
        };
      });

    modelCache[type] = models;
    modelCache.lastFetched[type] = new Date();

    return { success: true, models };
  } catch (error) {
    return {
      success: false,
      models: [],
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Fetch Venice's traits map (`default`, `fastest`, `most_intelligent`, …) which
 * Venice keeps pointed at the current best model per category. Used for
 * "smart default" resolution — never hardcoded.
 */
export async function fetchVeniceTraits(
  type: 'text' | 'image' = 'text',
): Promise<{ success: boolean; traits: Record<string, string>; error?: string }> {
  try {
    const { data, error } = await supabase.functions.invoke('venice-proxy', {
      body: { action: 'listTraits', options: { type } },
    });

    if (error) throw new Error(error.message);
    if (!data?.success) throw new Error(data?.error || 'Failed to fetch Venice traits');

    const traits: Record<string, string> = data.traits || {};
    modelCache.traits = traits;
    modelCache.lastFetched.traits = new Date();

    return { success: true, traits };
  } catch (error) {
    return {
      success: false,
      traits: {},
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

// ─── Cache accessors ─────────────────────────────────────────────────────────

export function getCachedModels(): { text: UnifiedModel[]; image: UnifiedModel[] } {
  return { text: modelCache.text, image: modelCache.image };
}

/** Resolve a readable display name for a model id (no name parsing). */
export function getModelDisplayName(modelId: string): string {
  const all = [...modelCache.text, ...modelCache.image];
  return all.find((m) => m.id === modelId)?.name || modelId;
}

// ─── Default resolution (dynamic, traits-driven) ─────────────────────────────

/**
 * Return the current default model for a type. Prefers Venice's `default`
 * trait, then `fastest`, then the first available model. Fully dynamic.
 */
export async function getEffectiveDefaultModel(type: 'text' | 'image'): Promise<string> {
  let list = modelCache[type];
  if (list.length === 0) {
    const res = await fetchVeniceModels(type);
    if (res.success) list = res.models;
  }
  if (list.length === 0) return '';

  // Prefer the `default` trait id if present and it exists in the list.
  if (modelCache.traits.default && list.some((m) => m.id === modelCache.traits.default)) {
    return modelCache.traits.default;
  }
  if (list.some((m) => m.traits?.includes('default'))) {
    return list.find((m) => m.traits?.includes('default'))!.id;
  }
  return list[0].id;
}

// ─── Price formatting (display only) ─────────────────────────────────────────

export function formatPrice(price: number | undefined, unit: string = '/1M tokens'): string {
  if (price === undefined || price === null) return 'N/A';
  if (price === 0) return 'Free';
  if (price < 0.01) return `$${(price * 100).toFixed(2)}¢${unit}`;
  return `$${price.toFixed(2)}${unit}`;
}

export function formatImagePrice(price: number | undefined): string {
  if (price === undefined || price === null) return 'N/A';
  if (price === 0) return 'Free';
  return `$${price.toFixed(2)}/image`;
}